<script setup lang="ts">
import {
  CHAPTER_TITLE_BASE_PT,
  CHAPTER_TITLE_EM_REF_PT,
} from "../export/chapterHeading";
import ZwFieldRow from "./ui/ZwFieldRow.vue";
import ChapterNumberingSettings from "./ChapterNumberingSettings.vue";
import type { ZwChapterNumbering } from "../lib/zwConfig";
import { just, nothing, isJust, isNothing, type Maybe } from "../lib/maybe";
import { computed, ref, useTemplateRef, watch } from "vue";
import { EDITOR_FONTS, fontStackFor } from "../editor/fonts";
import {
  chapterHeadingAssetName,
  importProjectAsset,
  pickImageFile,
  readProjectAssetDataUrl,
  removeProjectAsset,
} from "../lib/tauri/assets";
import { isTauri } from "../lib/runtime";
import type {
  ZwChapterHeadingStyle,
  ZwChapterTitleAlignment,
  ZwChapterTitleImagePosition,
  ZwFonts,
  ZwPageChrome,
  ZwFirstParagraph,
  ZwOpeningInitial,
  ZwOpeningLeadIn,
  ZwPageNumberPlacement,
  ZwParagraphFormatting,
  ZwParagraphStyle,
  ZwPrintSize,
  ZwSceneBreakStyle,
  ZwSceneFormatting,
} from "../lib/zwConfig";
import {
  CHAPTER_HEADING_SCALE_MAX,
  CHAPTER_HEADING_SCALE_MIN,
  CHAPTER_HEADING_SPACE_MAX,
  CHAPTER_HEADING_SPACE_MIN,
  CHAPTER_TITLE_ALIGNMENTS,
  CHAPTER_TITLE_IMAGE_POSITIONS,
  FIRST_PARAGRAPHS,
  OPENING_INITIALS,
  OPENING_LEAD_INS,
  PAGE_NUMBER_PLACEMENTS,
  PARAGRAPH_STYLES,
  PRINT_SIZES,
  SCENE_BREAK_STYLES,
  normalizeZwChapterHeadingStyle,
} from "../lib/zwConfig";
import ZwButton from "./ui/ZwButton.vue";
import ZwCheckbox from "./ui/ZwCheckbox.vue";
import ZwInput from "./ui/ZwInput.vue";
import ZwSelect from "./ui/ZwSelect.vue";

const chapterNumbering = defineModel<ZwChapterNumbering>("chapterNumbering", {
  required: true,
});
const actNumbering = defineModel<ZwChapterNumbering>("actNumbering", {
  required: true,
});
const fonts = defineModel<ZwFonts>("fonts", { required: true });
const printSize = defineModel<ZwPrintSize>("printSize", { required: true });
const paragraphFormatting = defineModel<ZwParagraphFormatting>(
  "paragraphFormatting",
  { required: true },
);
const chapterHeadingStyle = defineModel<ZwChapterHeadingStyle>(
  "chapterHeadingStyle",
  { required: true },
);
const pageChrome = defineModel<ZwPageChrome>("pageChrome", {
  required: true,
});
const sceneFormatting = defineModel<ZwSceneFormatting>("sceneFormatting", {
  required: true,
});

const props = defineProps<{
  rootPath: Maybe<string>;
}>();

const fontOptions = computed(() =>
  EDITOR_FONTS.map((font) => ({
    value: font.id,
    label: font.label,
    style: { fontFamily: fontStackFor(font.id) },
  })),
);

const paragraphStyleOptions = computed(() =>
  PARAGRAPH_STYLES.map((style) => ({ value: style.id, label: style.label })),
);

const firstParagraphOptions = computed(() =>
  FIRST_PARAGRAPHS.map((item) => ({ value: item.id, label: item.label })),
);

const openingInitialOptions = computed(() =>
  OPENING_INITIALS.map((item) => ({ value: item.id, label: item.label })),
);

const openingLeadInOptions = computed(() =>
  OPENING_LEAD_INS.map((item) => ({ value: item.id, label: item.label })),
);

const chapterTitleAlignmentOptions = computed(() =>
  CHAPTER_TITLE_ALIGNMENTS.map((item) => ({
    value: item.id,
    label: item.label,
  })),
);

const chapterTitleImagePositionOptions = computed(() =>
  CHAPTER_TITLE_IMAGE_POSITIONS.map((item) => ({
    value: item.id,
    label: item.label,
  })),
);

const paragraphStyle = computed({
  get: () => paragraphFormatting.value.style,
  set: (value: string) => {
    paragraphFormatting.value = {
      ...paragraphFormatting.value,
      style: value as ZwParagraphStyle,
    };
  },
});

const firstParagraph = computed({
  get: () => paragraphFormatting.value.firstParagraph,
  set: (value: string) => {
    paragraphFormatting.value = {
      ...paragraphFormatting.value,
      firstParagraph: value as ZwFirstParagraph,
    };
  },
});

const openingInitial = computed({
  get: () => paragraphFormatting.value.openingInitial,
  set: (value: string) => {
    paragraphFormatting.value = {
      ...paragraphFormatting.value,
      openingInitial: value as ZwOpeningInitial,
    };
  },
});

const openingLeadIn = computed({
  get: () => paragraphFormatting.value.openingLeadIn,
  set: (value: string) => {
    paragraphFormatting.value = {
      ...paragraphFormatting.value,
      openingLeadIn: value as ZwOpeningLeadIn,
    };
  },
});

const chapterTitleAlignment = computed({
  get: () => chapterHeadingStyle.value.alignment,
  set: (value: string) => {
    chapterHeadingStyle.value = {
      ...chapterHeadingStyle.value,
      alignment: value as ZwChapterTitleAlignment,
    };
  },
});

const chapterTitleImagePosition = computed({
  get: () => chapterHeadingStyle.value.imagePosition,
  set: (value: string) => {
    chapterHeadingStyle.value = {
      ...chapterHeadingStyle.value,
      imagePosition: value as ZwChapterTitleImagePosition,
    };
  },
});

function parseHeadingNumber(
  raw: string,
  min: number,
  max: number,
  fallback: number,
): number {
  const n = Number.parseFloat(raw.trim());
  if (Number.isFinite(n) === false) return fallback;
  return Math.min(max, Math.max(min, n));
}

// Present CSS pixels while retaining the existing project-file units.
const headingBasePx = (CHAPTER_TITLE_BASE_PT * 4) / 3;
const spacingBasePx = (CHAPTER_TITLE_EM_REF_PT * 4) / 3;
const pixelsText = (value: number): string =>
  String(Math.round(value * 100) / 100);

const spaceBeforeText = computed({
  get: () => pixelsText(chapterHeadingStyle.value.spaceBefore * spacingBasePx),
  set: (raw: string) => {
    chapterHeadingStyle.value = normalizeZwChapterHeadingStyle({
      ...chapterHeadingStyle.value,
      spaceBefore:
        parseHeadingNumber(
          raw,
          CHAPTER_HEADING_SPACE_MIN * spacingBasePx,
          CHAPTER_HEADING_SPACE_MAX * spacingBasePx,
          chapterHeadingStyle.value.spaceBefore * spacingBasePx,
        ) / spacingBasePx,
    });
  },
});

const spaceAfterText = computed({
  get: () => pixelsText(chapterHeadingStyle.value.spaceAfter * spacingBasePx),
  set: (raw: string) => {
    chapterHeadingStyle.value = normalizeZwChapterHeadingStyle({
      ...chapterHeadingStyle.value,
      spaceAfter:
        parseHeadingNumber(
          raw,
          CHAPTER_HEADING_SPACE_MIN * spacingBasePx,
          CHAPTER_HEADING_SPACE_MAX * spacingBasePx,
          chapterHeadingStyle.value.spaceAfter * spacingBasePx,
        ) / spacingBasePx,
    });
  },
});

const scaleText = computed({
  get: () => pixelsText(chapterHeadingStyle.value.scale * headingBasePx),
  set: (raw: string) => {
    chapterHeadingStyle.value = normalizeZwChapterHeadingStyle({
      ...chapterHeadingStyle.value,
      scale:
        parseHeadingNumber(
          raw,
          CHAPTER_HEADING_SCALE_MIN * headingBasePx,
          CHAPTER_HEADING_SCALE_MAX * headingBasePx,
          chapterHeadingStyle.value.scale * headingBasePx,
        ) / headingBasePx,
    });
  },
});

const headingSizeOptions = computed(() =>
  [
    ...new Set([
      14,
      16,
      18,
      20,
      24,
      28,
      32,
      36,
      40,
      48,
      Number(scaleText.value),
    ]),
  ]
    .sort((a, b) => a - b)
    .map((size) => ({ value: String(size), label: `${size} px` })),
);

const printSizeOptions = computed(() =>
  PRINT_SIZES.map((size) => ({
    value: size.id,
    label: size.label,
  })),
);

const pageNumberOptions = computed(() =>
  PAGE_NUMBER_PLACEMENTS.map((item) => ({
    value: item.id,
    label: item.label,
  })),
);

const pageNumber = computed({
  get: () => pageChrome.value.pageNumber,
  set: (value: string) => {
    pageChrome.value = {
      ...pageChrome.value,
      pageNumber: value as ZwPageNumberPlacement,
    };
  },
});

const sceneBreakStyleOptions = computed(() =>
  SCENE_BREAK_STYLES.map((item) => ({
    value: item.id,
    label: item.label,
  })),
);

const sceneBreakStyle = computed({
  get: () => sceneFormatting.value.breakStyle,
  set: (value: string) => {
    sceneFormatting.value = {
      ...sceneFormatting.value,
      breakStyle: value as ZwSceneBreakStyle,
    };
  },
});

const bodyFont = computed({
  get: () => fonts.value.body,
  set: (value: string) => {
    fonts.value = {
      ...fonts.value,
      body: value as ZwFonts["body"],
    };
  },
});

const headingFont = computed({
  get: () => fonts.value.heading,
  set: (value: string) => {
    fonts.value = {
      ...fonts.value,
      heading: value as ZwFonts["heading"],
    };
  },
});

const showRunningTitle = computed({
  get: () => pageChrome.value.showTitle,
  set: (value: boolean) => {
    pageChrome.value = { ...pageChrome.value, showTitle: value };
  },
});

const showRunningAuthor = computed({
  get: () => pageChrome.value.showAuthor,
  set: (value: boolean) => {
    pageChrome.value = { ...pageChrome.value, showAuthor: value };
  },
});

const showSceneTitles = computed({
  get: () => sceneFormatting.value.showSceneTitles,
  set: (value: boolean) => {
    sceneFormatting.value = {
      ...sceneFormatting.value,
      showSceneTitles: value,
    };
  },
});

const sceneBreakCustomText = computed({
  get: () => sceneFormatting.value.breakCustomText,
  set: (value: string) => {
    sceneFormatting.value = {
      ...sceneFormatting.value,
      breakCustomText: value,
    };
  },
});

const bodyFontSelect =
  useTemplateRef<InstanceType<typeof ZwSelect>>("bodyFontSelect");

const choosingHeadingImage = ref(false);
const headingImagePreviewSrc = ref("");
let headingPreviewToken = 0;

const canManageHeadingImage = computed(() => {
  const root = props.rootPath;
  return isJust(root) && root.value.length > 0 && isTauri();
});

const hasHeadingImage = computed(
  () => chapterHeadingStyle.value.image.trim().length > 0,
);

watch(
  () => chapterHeadingStyle.value.image,
  async (relative) => {
    const token = ++headingPreviewToken;
    const trimmed = relative.trim();
    const root = props.rootPath;
    if (
      trimmed.length === 0 ||
      isNothing(root) ||
      root.value.length === 0 ||
      isTauri() === false
    ) {
      headingImagePreviewSrc.value = "";
      return;
    }
    try {
      const src = await readProjectAssetDataUrl(root.value, trimmed);
      if (token === headingPreviewToken) {
        headingImagePreviewSrc.value = src;
      }
    } catch {
      if (token === headingPreviewToken) {
        headingImagePreviewSrc.value = "";
      }
    }
  },
  { immediate: true },
);

async function chooseHeadingImage(): Promise<void> {
  const root = props.rootPath;
  if (
    isNothing(root) ||
    canManageHeadingImage.value === false ||
    choosingHeadingImage.value === true
  ) {
    return;
  }
  const source = await pickImageFile();
  if (source === null) return;

  choosingHeadingImage.value = true;
  const previous = chapterHeadingStyle.value.image.trim();
  try {
    const assetName = chapterHeadingAssetName(source);
    const relative = await importProjectAsset(root.value, source, assetName);
    if (previous.length > 0 && previous !== relative) {
      await removeProjectAsset(root.value, previous);
    }
    chapterHeadingStyle.value = {
      ...chapterHeadingStyle.value,
      image: relative,
    };
  } catch (error) {
    console.error("Failed to import chapter title image", error);
    window.alert(
      error instanceof Error
        ? error.message
        : "Could not add chapter title image.",
    );
  } finally {
    choosingHeadingImage.value = false;
  }
}

function removeHeadingImage(): void {
  chapterHeadingStyle.value = {
    ...chapterHeadingStyle.value,
    image: "",
  };
}

defineExpose({
  focus: () => bodyFontSelect.value?.focus(),
});
</script>

<template>
  <section class="project-settings-group" aria-label="Fonts">
    <h3 class="project-settings-subhead">Fonts</h3>
    <p class="project-settings-hint">
      Used in your manuscript and exported books.
    </p>
    <ZwFieldRow>
      <ZwSelect
        :id="nothing"
        ref="bodyFontSelect"
        v-model="bodyFont"
        :label="just('Body font')"
        :options="just(fontOptions)"
      />
      <ZwSelect
        :id="nothing"
        v-model="headingFont"
        :label="just('Heading font')"
        :options="just(fontOptions)"
      />
    </ZwFieldRow>
    <ZwSelect
      :id="nothing"
      v-model="printSize"
      :label="just('Print size')"
      :options="just(printSizeOptions)"
    />
  </section>
  <section class="project-settings-group" aria-label="Paragraphs">
    <h3 class="project-settings-subhead">Paragraphs</h3>
    <ZwFieldRow>
      <ZwSelect
        :id="nothing"
        v-model="paragraphStyle"
        :label="just('Paragraph style')"
        :options="just(paragraphStyleOptions)"
      />
      <ZwSelect
        v-if="paragraphStyle === 'indented'"
        :id="nothing"
        v-model="firstParagraph"
        :label="just('First paragraph indentation')"
        :options="just(firstParagraphOptions)"
      />
    </ZwFieldRow>
    <p class="project-settings-hint">
      Give each chapter’s opening paragraph a larger first letter, small caps,
      or both.
    </p>
    <ZwFieldRow>
      <ZwSelect
        :id="nothing"
        v-model="openingInitial"
        :label="just('Chapter initial')"
        :options="just(openingInitialOptions)"
      />
      <ZwSelect
        :id="nothing"
        v-model="openingLeadIn"
        :label="just('Chapter small caps')"
        :options="just(openingLeadInOptions)"
      />
    </ZwFieldRow>
  </section>
  <section class="project-settings-group" aria-label="Act headings">
    <h3 class="project-settings-subhead">Act headings</h3>
    <ChapterNumberingSettings v-model="actNumbering" kind="Act" />
  </section>
  <section class="project-settings-group" aria-label="Chapter headings">
    <h3 class="project-settings-subhead">Chapter headings</h3>
    <h4 class="project-settings-label">Numbering</h4>
    <ChapterNumberingSettings v-model="chapterNumbering" kind="Chapter" />
    <h4 class="project-settings-label">Appearance</h4>
    <ZwFieldRow>
      <ZwSelect
        :id="nothing"
        v-model="chapterTitleAlignment"
        :label="just('Alignment')"
        :options="just(chapterTitleAlignmentOptions)"
      />
      <ZwSelect
        :id="nothing"
        v-model="scaleText"
        :label="just('Heading size')"
        :options="just(headingSizeOptions)"
      />
    </ZwFieldRow>
    <ZwFieldRow>
      <ZwInput
        :id="nothing"
        v-model="spaceBeforeText"
        :label="just('Space above (px)')"
        placeholder="0"
      />
      <ZwInput
        :id="nothing"
        v-model="spaceAfterText"
        :label="just('Space below (px)')"
        placeholder="12.8"
      />
    </ZwFieldRow>

    <div class="chapter-title-image-field">
      <span v-if="hasHeadingImage" class="zw-field-label">Heading image</span>
      <img
        v-if="headingImagePreviewSrc.length > 0"
        :src="headingImagePreviewSrc"
        alt="Chapter heading image"
        class="chapter-title-image-preview"
      />
      <p v-else-if="hasHeadingImage" class="project-settings-hint">
        {{ chapterHeadingStyle.image }}
      </p>
      <div class="chapter-title-image-actions">
        <ZwButton
          size="sm"
          :disabled="!canManageHeadingImage || choosingHeadingImage"
          @click="chooseHeadingImage"
        >
          {{
            choosingHeadingImage
              ? "Adding…"
              : hasHeadingImage
                ? "Replace"
                : "Add heading image"
          }}
        </ZwButton>
        <ZwButton
          v-if="hasHeadingImage"
          size="sm"
          :disabled="choosingHeadingImage"
          @click="removeHeadingImage"
        >
          Remove
        </ZwButton>
      </div>
    </div>
    <ZwSelect
      v-if="hasHeadingImage"
      :id="nothing"
      v-model="chapterTitleImagePosition"
      :label="just('Image position')"
      :options="just(chapterTitleImagePositionOptions)"
    />
  </section>
  <section
    class="project-settings-group"
    aria-label="Headers &amp; page numbers"
  >
    <h3 class="project-settings-subhead">Headers &amp; page numbers</h3>
    <p class="project-settings-hint">
      Printed on body pages. Title pages and chapter openings stay clear.
    </p>
    <div class="zw-field">
      <span class="zw-field-label">Page headers</span>
      <div class="project-settings-checks">
        <ZwCheckbox v-model="showRunningTitle">
          Book title on right-hand pages
        </ZwCheckbox>
        <ZwCheckbox v-model="showRunningAuthor">
          Author on left-hand pages
        </ZwCheckbox>
      </div>
    </div>
    <ZwSelect
      :id="nothing"
      v-model="pageNumber"
      :label="just('Page numbers')"
      :options="just(pageNumberOptions)"
    />
  </section>
  <section class="project-settings-group" aria-label="Scene breaks">
    <h3 class="project-settings-subhead">Scene breaks</h3>
    <p class="project-settings-hint">
      Separate scenes within a chapter in exported books.
    </p>
    <div class="zw-field">
      <span class="zw-field-label">Scene titles</span>
      <div class="project-settings-checks">
        <ZwCheckbox v-model="showSceneTitles">
          Show scene titles in export
        </ZwCheckbox>
      </div>
    </div>
    <ZwSelect
      :id="nothing"
      v-model="sceneBreakStyle"
      :label="just('Scene break')"
      :options="just(sceneBreakStyleOptions)"
    />
    <ZwInput
      v-if="sceneFormatting.breakStyle === 'custom'"
      :id="nothing"
      v-model="sceneBreakCustomText"
      :label="just('Custom scene break text')"
      placeholder="e.g. ~"
    />
  </section>
</template>

<style scoped>
.chapter-title-image-field {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  font-family: var(--font-ui);
  color: var(--text-muted);
}

.chapter-title-image-preview {
  display: block;
  max-width: 10rem;
  max-height: 4rem;
  width: auto;
  height: auto;
  object-fit: contain;
  border: 1px solid var(--line);
  background: var(--bg);
}

.chapter-title-image-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
</style>
